import { useToast } from "@medimo/ui";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import { toCollaboLinkQuery } from "../collabo-link-return";
import {
  type CollaboLinkErrorCode,
  type CollaboLinkResult,
  type CollaboPortalPendingInfo,
  LINK_IN_PROGRESS_RETRY,
  postCollaboPortalLink,
} from "../collabo-portal";
import {
  clearParentDomainAmplifyCookies,
  consumeCollaboLinkFailedCookie,
  markCollaboLinkAbort,
} from "./amplify-cookie-utils";
import { redirectToCollaboOidcStart } from "./collabo-oidc-start";

const COLLABO_LINK_MODE_KEY = "collaboLinkMode";
const COLLABO_LINK_EMAIL_KEY = "collaboLinkEmail";
const COLLABO_OIDC_RESTART_KEY = "collaboOidcRestartAttempts";
const COLLABO_PENDING_INFO_KEY = "collaboPendingInfo";
const COLLABO_TERMS_KEY = "collaboTermsAgreed";
const COLLABO_LINK_REF_KEY = "collaboLinkRef";
const CollaboPendingInfoSchema = z.object({
  name: z.string().nullable(),
  email: z.string().nullable(),
});
const MAX_OIDC_RESTARTS = 1;
const SIGN_IN_PATH = "/auth/sign-in";
// The sign-in page reads this query to render link mode; the NEW_PASSWORD / TWO_FACTOR screens
// store it so "back to sign-in" returns to link mode. It grants nothing: every link request
// still needs the HttpOnly pending cookie issued by the BE.
const COLLABO_LINK_QUERY = "collabo_flow=portal_link";

const getOidcRestartCount = (): number => {
  try {
    return Number.parseInt(
      sessionStorage.getItem(COLLABO_OIDC_RESTART_KEY) ?? "0",
      10,
    );
  } catch {
    return 0;
  }
};

const incrementOidcRestartCount = (): void => {
  try {
    sessionStorage.setItem(
      COLLABO_OIDC_RESTART_KEY,
      String(getOidcRestartCount() + 1),
    );
  } catch {
    // Without storage we lose the loop guard, but allow one attempt.
  }
};

// Deliberately NOT part of clearCollaboLinkMode(): the sign-in screen runs that on every mount,
// and a COLLABO_PENDING_EXPIRED restart routes back through the sign-in screen (redirectTo
// CollaboOidcStart → BE callback → /auth/sign-in). Clearing the counter there would reset it on
// every pass and let a persistently-expired pending loop forever. Cleared only at the real
// end-of-attempt points (landInApp / abortToSignIn / cancelLink).
const clearOidcRestartCount = (): void => {
  try {
    sessionStorage.removeItem(COLLABO_OIDC_RESTART_KEY);
  } catch {
    // Nothing to clean up when storage is unavailable.
  }
};

const readCollaboLinkQuery = (): string | null => {
  try {
    const raw = sessionStorage.getItem(COLLABO_LINK_MODE_KEY);
    return raw === null ? null : toCollaboLinkQuery(new URLSearchParams(raw));
  } catch {
    return null;
  }
};

export const isCollaboLinkMode = (): boolean => readCollaboLinkQuery() !== null;

export const readCollaboLinkEmail = (): string => {
  try {
    return sessionStorage.getItem(COLLABO_LINK_EMAIL_KEY) ?? "";
  } catch {
    return "";
  }
};

// Fallback for when GET /v3/collabo/portal/pending fails (pending TTL elapsed, network):
// the sign-in screen tries the real endpoint on every mount first and only falls
// back to this cached value on failure, so a fresh/different pending (e.g. after an OIDC
// restart) always overrides it rather than showing stale data forever. Display-only, same as
// the value it caches; never sent to any API. Not read by Datadog RUM / PostHog (they only
// ever capture the page URL), so this does not reopen the PII-in-URL issue the pending
// endpoint was built to fix.
export const readCollaboPendingInfo = (): CollaboPortalPendingInfo | null => {
  try {
    const raw = sessionStorage.getItem(COLLABO_PENDING_INFO_KEY);
    if (raw === null) return null;
    const result = CollaboPendingInfoSchema.safeParse(JSON.parse(raw));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
};

export const writeCollaboPendingInfo = (
  info: CollaboPortalPendingInfo,
): void => {
  try {
    sessionStorage.setItem(COLLABO_PENDING_INFO_KEY, JSON.stringify(info));
  } catch {
    // Without storage, a reload after the pending expires just loses the prefill.
  }
};

const setCollaboLinkMode = (query: string): void => {
  try {
    sessionStorage.setItem(COLLABO_LINK_MODE_KEY, query);
  } catch {
    // Without storage the next screen falls back to normal mode and the flow restarts.
  }
};

export const clearCollaboLinkMode = (): void => {
  try {
    sessionStorage.removeItem(COLLABO_LINK_MODE_KEY);
    sessionStorage.removeItem(COLLABO_LINK_EMAIL_KEY);
  } catch {
    // Nothing to clean up when storage is unavailable.
  }
};

// Deliberately NOT part of clearCollaboLinkMode(): that call also runs on every plain mount
// of the sign-in screen (outside link mode), which would wipe this cache before a reload ever
// gets a chance to read it back — defeating the one case it exists for. Call this only at the
// real end-of-attempt points (landInApp / abortToSignIn) so a later attempt (possibly a
// different Collabo ID) never starts out showing a stale cached name.
export const clearCollaboPendingInfo = (): void => {
  try {
    sessionStorage.removeItem(COLLABO_PENDING_INFO_KEY);
  } catch {
    // Nothing to clean up when storage is unavailable.
  }
};

// The pending cookie is one per browser, so a link started in another tab with a different
// Collabo ID replaces it. The tab keeps the link_ref of the Collabo ID it first showed and
// sends it with POST /link, so the BE refuses to link the replaced pending to the account
// entered here. Dropped when this tab restarts OIDC itself: the user may then sign in as a
// different Collabo ID on purpose, and the link-mode screen shows that ID before anything is
// entered, so the next GET pending stores the new value instead of refusing it.
const readCollaboLinkRef = (): string | null => {
  try {
    return sessionStorage.getItem(COLLABO_LINK_REF_KEY);
  } catch {
    return null;
  }
};

const clearCollaboLinkRef = (): void => {
  try {
    sessionStorage.removeItem(COLLABO_LINK_REF_KEY);
  } catch {
    // Nothing to clean up when storage is unavailable.
  }
};

// The first value seen is kept rather than overwritten, or a reload after the swap would
// adopt the other Collabo ID and pass.
const checkCollaboLinkRef = (
  linkRef: string | null,
): "match" | "mismatch" | "unavailable" => {
  try {
    const stored = sessionStorage.getItem(COLLABO_LINK_REF_KEY);
    if (linkRef === null) {
      // Nothing to compare with: the submit checks again, or restarts OIDC without a value.
      return "match";
    }
    if (stored === null) {
      sessionStorage.setItem(COLLABO_LINK_REF_KEY, linkRef);
      return "match";
    }
    return stored === linkRef ? "match" : "mismatch";
  } catch {
    // Fail closed: a value held only in memory would be lost on reload, reopening the swap.
    return "unavailable";
  }
};

// The set-initial-password form is the only screen with the terms checkbox. When the first
// sign-in needs TOTP enrolment, Amplify routes set-password → two-factor, so completeLink()
// runs on a later screen than where the terms were ticked. The agreement is parked here and
// read back when the link request finally fires.
const markTermsAgreed = (): void => {
  try {
    sessionStorage.setItem(COLLABO_TERMS_KEY, "true");
  } catch {
    // Without storage the terms flag is lost; the BE treats a missing agreement as not-agreed.
  }
};

const readTermsAgreed = (): boolean => {
  try {
    return sessionStorage.getItem(COLLABO_TERMS_KEY) === "true";
  } catch {
    return false;
  }
};

const clearTermsAgreed = (): void => {
  try {
    sessionStorage.removeItem(COLLABO_TERMS_KEY);
  } catch {
    // Nothing to clean up when storage is unavailable.
  }
};

// Same fallback wording the regular sign-in form shows for a disabled account.
const SIGN_IN_UNAVAILABLE_MESSAGE =
  "ログインできませんでした。しばらく時間をおいてから再度お試しください。解決しない場合は「パスワードを忘れた方はこちら」からパスワードを再発行するか、サポートへお問い合わせください。";

export const COLLABO_LINK_ABORT_MESSAGES = {
  failed: "Collaboとの連携に失敗しました。時間をおいて再度お試しください。",
  session_expired:
    "セッションの有効期限が切れました。お手数ですが、再度ログインしてください。",
  sign_in_error: "エラーが発生しました。もう一度ログインしてください。",
  link_conflict: "このIDとは連携できません。",
  user_disabled: SIGN_IN_UNAVAILABLE_MESSAGE,
  retry_from_portal:
    "連携処理を完了できませんでした。お手数ですが、コラボポータルからもう一度アクセスしてください。",
  pending_changed:
    "別のコラボIDで連携が開始されたため、この画面からは連携できません。お手数ですが、コラボポータルから再度アクセスしてください。",
} as const;

// Resolves true at once when the browser is online, otherwise when it reports the connection is
// back. Resolves false when `signal` aborts first, so the caller can stop without acting.
// Navigating while offline cannot fetch the target page, so Next.js falls back to a full page load
// and the browser shows its own error page (ERR_INTERNET_DISCONNECTED) instead of the app.
const waitForOnline = (signal?: AbortSignal): Promise<boolean> => {
  if (navigator.onLine) {
    return Promise.resolve(!signal?.aborted);
  }
  return new Promise((resolve) => {
    const cleanup = () => {
      window.removeEventListener("online", onOnline);
      signal?.removeEventListener("abort", onAbort);
    };
    const onOnline = () => {
      cleanup();
      resolve(true);
    };
    const onAbort = () => {
      cleanup();
      resolve(false);
    };
    window.addEventListener("online", onOnline);
    signal?.addEventListener("abort", onAbort);
  });
};

// Resolves true after `ms`, or false as soon as `signal` aborts.
const waitFor = (ms: number, signal: AbortSignal): Promise<boolean> => {
  if (signal.aborted) {
    return Promise.resolve(false);
  }
  return new Promise((resolve) => {
    const onAbort = () => {
      clearTimeout(timer);
      resolve(false);
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve(true);
    }, ms);
    signal.addEventListener("abort", onAbort);
  });
};

const isLinkInProgress = (result: CollaboLinkResult): boolean =>
  result.kind === "error" && result.code === "LINK_IN_PROGRESS";

// Waits until `result` may be sent again. Resolves false when it is final or `signal` aborts.
const waitToResend = (
  result: CollaboLinkResult,
  inProgressRetries: number,
  signal: AbortSignal,
): Promise<boolean> => {
  if (result.kind === "network_error" && !navigator.onLine) {
    return waitForOnline(signal);
  }
  if (
    isLinkInProgress(result) &&
    inProgressRetries < LINK_IN_PROGRESS_RETRY.maxRetries
  ) {
    return waitFor(LINK_IN_PROGRESS_RETRY.delayMs, signal);
  }
  return Promise.resolve(false);
};

// POSTs /link. While the browser is offline and the call got no answer, leaving the screen would
// only show the browser's error page and, since abort tears the Amplify session down, force the
// user back to the Portal. So it waits for the connection and sends /link again with a fresh id
// token. Online with no answer (server down, timeout) is not retried: navigating works, so the
// caller fails it like any other unreadable answer.
// A resend can reach the BE while the lost first /link is still calling Collabo register; the BE
// then answers LINK_IN_PROGRESS, so it waits and resends until that attempt settles. Once the
// retries are spent the LINK_IN_PROGRESS answer is returned and fails as an unknown error, not as
// a conflict. Resolves null when `signal` aborts first.
const sendLink = async (
  idToken: string,
  linkRef: string,
  terms: boolean,
  signal: AbortSignal,
): Promise<CollaboLinkResult | null> => {
  let result = await postCollaboPortalLink(idToken, linkRef, terms);
  let inProgressRetries = 0;
  while (await waitToResend(result, inProgressRetries, signal)) {
    if (isLinkInProgress(result)) {
      inProgressRetries += 1;
    }
    const refreshed = await fetchAuthSession();
    result = await postCollaboPortalLink(
      refreshed.tokens?.idToken?.toString() ?? idToken,
      linkRef,
      terms,
    );
    if (signal.aborted) {
      return null;
    }
  }
  return signal.aborted ? null : result;
};

export type CollaboLinkAbortReason = keyof typeof COLLABO_LINK_ABORT_MESSAGES;

export const isCollaboLinkAbortReason = (
  value: string | null,
): value is CollaboLinkAbortReason =>
  value !== null && Object.hasOwn(COLLABO_LINK_ABORT_MESSAGES, value);

const LINK_FAILED_MESSAGE =
  "Collabo IDとの連携に失敗しました。次回ポータルからアクセスした際に再度連携されます。";

const withReason = (
  path: string,
  reason: CollaboLinkAbortReason | undefined,
) =>
  reason === undefined
    ? path
    : `${path}${path.includes("?") ? "&" : "?"}collabo_link=${reason}`;

export const useCollaboLinkFlow = () => {
  const router = useRouter();
  const { toast } = useToast();
  // A second link attempt racing the first would only earn a 409, so it is dropped here.
  const inFlight = useRef(false);
  const [isLinking, setIsLinking] = useState(false);
  // Cancels a /link that is waiting to be re-sent (offline, back-off) when the user leaves the step
  // with 戻る or the screen unmounts, so no ghost retry runs afterwards.
  const linkAbort = useRef<AbortController | null>(null);
  useEffect(() => () => linkAbort.current?.abort(), []);

  // Terminal success path: the medimo session is THIS tab's own Amplify host-only session from
  // signIn. /link is confirm-only (returns 200 with no Set-Cookie), so there is no server-issued
  // session to reconcile here — the app reads the signIn session directly.
  const landInApp = useCallback(
    (linkFailed: boolean) => {
      clearCollaboLinkMode();
      clearCollaboPendingInfo();
      clearCollaboLinkRef();
      clearTermsAgreed();
      clearOidcRestartCount();
      // Always consumed, so a stale cookie cannot resurface on a later sign-in.
      if (consumeCollaboLinkFailedCookie() || linkFailed) {
        toast({ variant: "destructive", title: LINK_FAILED_MESSAGE });
      }
      try {
        sessionStorage.setItem("showNotificationDialog", "true");
      } catch {
        // The dialog is a convenience; the session is already established.
      }
      // Navigation is under way, so inFlight is deliberately left set.
      router.replace("/");
    },
    [router, toast],
  );

  // Terminal failure path: the only live session is THIS tab's Amplify host-only session from
  // signIn, so it must be torn down before returning to sign-in, unlike the old server-side
  // flow which never established an Amplify session here.
  const abortToSignIn = useCallback(
    async (reason: CollaboLinkAbortReason, keepLinkMode: boolean) => {
      clearCollaboLinkMode();
      clearCollaboPendingInfo();
      // Staying in link mode keeps the same pending, so the tab keeps the Collabo ID it checks.
      if (!keepLinkMode) {
        clearCollaboLinkRef();
      }
      clearTermsAgreed();
      clearOidcRestartCount();
      markCollaboLinkAbort();
      await signOut().catch(() => undefined);
      clearParentDomainAmplifyCookies();
      inFlight.current = false;
      setIsLinking(false);
      router.replace(
        withReason(
          keepLinkMode ? `${SIGN_IN_PATH}?${COLLABO_LINK_QUERY}` : SIGN_IN_PATH,
          reason,
        ),
      );
    },
    [router],
  );

  const handleLinkError = useCallback(
    (code: CollaboLinkErrorCode): Promise<void> | void => {
      if (code === "COLLABO_PENDING_EXPIRED") {
        // If the Keycloak SSO session is still alive, a transparent OIDC restart issues a
        // fresh Collabo access token and a new pending without a Collabo re-login. Guard with
        // a counter so a persistently broken backend cannot loop indefinitely.
        if (getOidcRestartCount() < MAX_OIDC_RESTARTS) {
          incrementOidcRestartCount();
          // This tab starts the restart, so whichever Collabo ID signs in next is the user's
          // own choice, not another tab replacing the pending.
          clearCollaboLinkRef();
          redirectToCollaboOidcStart({ resumeChallenge: true });
          return;
        }
        return abortToSignIn("retry_from_portal", false);
      }
      if (code === "COLLABO_PENDING_CHANGED") {
        // Another tab replaced the browser-wide pending with a different Collabo ID. Restarting
        // OIDC here would just pick that ID up, so the user restarts from the Portal instead.
        return abortToSignIn("pending_changed", false);
      }
      if (code === "LINK_CONFLICT") {
        // Stay in link mode: the user can sign in with a different medimo account.
        return abortToSignIn("link_conflict", true);
      }
      // INVALID_CREDENTIALS, SESSION_ISSUE_FAILED, UNKNOWN, and LINK_IN_PROGRESS once sendLink
      // spent its resends: the row may still be held by a crashed attempt, which is no conflict.
      return abortToSignIn("failed", false);
    },
    [abortToSignIn],
  );

  const handleLinkResult = useCallback(
    async (result: CollaboLinkResult): Promise<void> => {
      switch (result.kind) {
        case "linked":
          landInApp(false);
          return;
        case "link_failed":
          landInApp(true);
          return;
        case "network_error":
          // Online but still no answer (server down, timeout): navigating works, so fail as
          // any other unreadable answer does.
          await handleLinkError("UNKNOWN");
          return;
        case "error":
          await handleLinkError(result.code);
          return;
      }
    },
    [handleLinkError, landInApp],
  );

  const completeLink = useCallback(
    async (isTermsOfUseAgreed?: boolean) => {
      if (inFlight.current) {
        return;
      }
      inFlight.current = true;
      setIsLinking(true);
      const controller = new AbortController();
      linkAbort.current = controller;
      const { signal } = controller;
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (idToken === undefined) {
          throw new Error("No id token after sign-in.");
        }
        const terms = isTermsOfUseAgreed ?? readTermsAgreed();
        // The reference of the Collabo ID this tab first showed. Without one the tab never saw
        // the pending, so nothing is sent and the flow takes the expired path (a transparent
        // OIDC restart issues a new pending and its reference).
        const linkRef = readCollaboLinkRef();
        if (linkRef === null) {
          await handleLinkError("COLLABO_PENDING_EXPIRED");
          return;
        }
        const result = await sendLink(idToken, linkRef, terms, signal);
        if (result === null) {
          return;
        }
        await handleLinkResult(result);
      } catch {
        // signIn already succeeded, so a throw here (no id token in the session, or a network
        // blip on fetchAuthSession) is exceptional. The form callbacks are fire-and-forget
        // (onSuccessToSignIn/onSetInitialPassword are not awaited), so rethrowing would become
        // an unhandled rejection rather than a form error. Abort to sign-in instead, which
        // tears down the live Amplify session and shows the generic failure message.
        if (signal.aborted) {
          return;
        }
        await abortToSignIn("failed", false);
      }
    },
    [abortToSignIn, handleLinkError, handleLinkResult],
  );

  // The new-password / two-factor screens read the stored link query to render link mode.
  const enterNewPasswordStep = useCallback(
    (email: string) => {
      try {
        sessionStorage.setItem(COLLABO_LINK_EMAIL_KEY, email);
      } catch {
        // Best-effort: the email is only used for display on the next screen.
      }
      setCollaboLinkMode(COLLABO_LINK_QUERY);
      router.replace("/auth/set-initial-password");
    },
    [router],
  );

  const enterTwoFactorStep = useCallback(() => {
    setCollaboLinkMode(COLLABO_LINK_QUERY);
    router.replace("/auth/two-factor");
  }, [router]);

  // Leaving a step the user chose to abandon (or a sign-in error during a First-Link step): the
  // live Amplify session must be signed out, same as abortToSignIn but without an error reason.
  // Return to the link-mode sign-in (query preserved), not plain sign-in: the sign-in screen
  // reads link mode only from the URL, and the HttpOnly pending cookie is still valid, so the
  // user can re-sign-in and finish the link without redoing OIDC from Collabo Portal.
  const cancelLink = useCallback(async () => {
    linkAbort.current?.abort();
    clearCollaboLinkMode();
    // The cached Collabo name/email is kept: the tab returns to the same Collabo ID (its link_ref
    // is kept too), and the pending often expired while the challenge was open, so GET pending
    // answers 410 and the cache is all the panel has.
    clearTermsAgreed();
    clearOidcRestartCount();
    await signOut().catch(() => undefined);
    clearParentDomainAmplifyCookies();
    inFlight.current = false;
    setIsLinking(false);
    router.replace(`${SIGN_IN_PATH}?${COLLABO_LINK_QUERY}`);
  }, [router]);

  // Run on every mount of the link-mode sign-in, before the Collabo ID is shown. Returns whether
  // the tab left link mode; the caller keeps its loader until the URL changes.
  const leaveOnLinkRefMismatch = useCallback(
    (linkRef: string | null): boolean => {
      const check = checkCollaboLinkRef(linkRef);
      if (check === "match") {
        return false;
      }
      clearCollaboLinkMode();
      clearCollaboPendingInfo();
      clearCollaboLinkRef();
      inFlight.current = false;
      markCollaboLinkAbort();
      router.replace(
        withReason(
          SIGN_IN_PATH,
          check === "mismatch" ? "pending_changed" : "retry_from_portal",
        ),
      );
      return true;
    },
    [router],
  );

  return {
    completeLink,
    leaveOnLinkRefMismatch,
    isLinking,
    enterNewPasswordStep,
    enterTwoFactorStep,
    markTermsAgreed,
    cancelLink,
  };
};
